// src/components/Hero/Hero.jsx
'use client'

import HeroSlider from '@/components/Hero/SliderLoop'
import useAdjustFontSizes from '@/hooks/useAdjustFontSizes'
import { Link } from '@/i18n/navigation'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

const MOBILE_BREAKPOINT = 768;

/**
 * Hero component (client-rendered with font size adjustment)
 *
 * @param {{ heroData: {
 *   heading: string
 *   subHeading: string
 *   description: string
 *   mobileDescription?: string
 *   buttonText: string
 *   list: string[]
 *   image: { src: string, width: number, height: number, blurDataURL?: string }
 * } }} props
 */
export default function Hero({ heroData }) {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Desktop elements
  const desktopElements = {
    heading: {
      ref: useRef(null),
      maxFontSize: 42,
      // maxWidth: 570,
      maxHeight: 120,
      adjustLineHeight: false
    },
    subHeading: {
      ref: useRef(null),
      maxFontSize: 42,
      maxWidth: 570
    },
    description: {
      ref: useRef(null),
      maxFontSize: 18,
      maxHeight: 48,
      adjustLineHeight: false
    }
  };

  // Mobile elements
  const mobileElements = {
    heading: {
      ref: useRef(null),
      maxFontSize: 36,
      maxHeight: 120,
      adjustLineHeight: false
    },
    mobileDescription: {
      ref: useRef(null),
      maxFontSize: 18,
      maxHeight: 32
    }
  };

  const [desktopFontSizes, setDesktopFontSizes] = useState({
    heading: 48,
    subHeading: 48,
    description: 18
  });

  const [mobileFontSizes, setMobileFontSizes] = useState({
    heading: 36,
    mobileDescription: 17
  });


  // Apply font size adjustment for desktop
  useAdjustFontSizes(
    desktopElements,
    [heroData.heading, heroData.description],
    setDesktopFontSizes
  );



  // Apply font size adjustment for mobile
  useAdjustFontSizes(
    mobileElements,
    [heroData.heading, heroData.subHeading, heroData.mobileDescription, isMobile],
    setMobileFontSizes
  );

  return (
    <section className="flex flex-col">
      {/* Mobile slider - full width, outside container */}
      <div className="md:hidden mt-8">
        <HeroSlider />
      </div>

      <div className="flex justify-between px-4 lg:px-0 pt-6 md:pt-8 bg-white max-w-[910px] mx-auto flex-col md:flex-row gap-12">
        {/* LEFT COLUMN: text + CTA + features */}
        <div className=" flex flex-col gap-6 md:gap-0 flex-1">
          {/* Desktop heading */}
          <h1 className='hidden md:block'>
            <div
              className="font-bold text-[#2B2B2B] font-roboto md:font-inter whitespace-normal gap-2"
              ref={desktopElements.heading.ref}
              style={{
                fontSize: `${desktopFontSizes.heading.fontSize}px`,
                lineHeight: '1.3em'
              }}
              dangerouslySetInnerHTML={{ __html: heroData.heading }}
            />
            {/* <div
              ref={desktopElements.subHeading.ref}
              style={{
                fontSize: `${ desktopFontSizes.subHeading } px`,
                lineHeight: '1.15em'
              }}
              dangerouslySetInnerHTML={{ __html: heroData.subHeading }}
            /> */}
          </h1>

          {/* Mobile heading */}
          <h1 className="font-bold text-[#2B2B2B] font-roboto md:font-inter md:hidden px-6">
            <div
              ref={mobileElements.heading.ref}
              style={{
                fontSize: `${mobileFontSizes.heading} px`,
                lineHeight: '1.3em'
              }}
              dangerouslySetInnerHTML={{ __html: heroData.heading }}
            />
          </h1>

          {/* Desktop description */}
          <p
            ref={desktopElements.description.ref}
            className="hidden md:block mt-2 mb-4 text-[#2B2B2B]"
            style={{
              fontSize: `${desktopFontSizes.description} px`,
              lineHeight: '2.67em'
            }}
          >
            {heroData.description}
          </p>

          {/* Mobile description */}
          {heroData.mobileDescription && (
            <p
              ref={mobileElements.mobileDescription.ref}
              className="md:hidden -mt-4 text-[#2B2B2B] text-center"
              style={{
                fontSize: `${mobileFontSizes.mobileDescription} px`,
                lineHeight: '1.41em'
              }}
            >
              {heroData.mobileDescription}
            </p>
          )}

          {/* CTA button + features */}
          <div className="flex flex-col mt-2">
            <Link
              href="/create-your-book"
              className="max-w-[330px] w-[264px] md:w-[236px] h-[54px] mx-auto flex justify-center items-center bg-[#2B2B2B] text-white font-semibold text-[21px] md:text-[18px] rounded-[10.3px] gap-2 shadow-heroBtnShadow group btn-primary"
            >
              <span className="mb-[2px]">{heroData.buttonText}</span>
              <span className="transform group-hover:translate-x-1.5 transition">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.38281 1.47266L10.8996 6.14064C11.0199 6.2649 11.0202 6.46204 10.9004 6.58672L6.38281 11.2879" stroke="white" strokeWidth="2.04485" strokeLinecap="round" />
                  <path d="M1.48438 1.47266L6.00119 6.14064C6.12143 6.2649 6.12177 6.46204 6.00196 6.58672L1.48438 11.2879" stroke="white" strokeWidth="2.04485" strokeLinecap="round" />
                </svg>
              </span>
            </Link>

            {/* Books created banner */}
            <div
              className="mt-6 -mx-4 bg-[#FFE098] py-3 text-center text-[#2B2B2B] font-bold tracking-[0.05em]"
              style={{
                fontSize: '18px',
                fontVariantCaps: 'all-small-caps',
                width: 'calc(100% + 2rem)',
              }}
            >
              {heroData.booksCreated}
            </div>

            {/* Trustpilot widget */}
            <a className="mt-8 w-full flex justify-center items-center text-[13px] md:text-[15px] md:gap-5 gap-3" href="https://www.trustpilot.com/review/booktailor.com" target="_blank" rel="noopener noreferrer">
              <div className="whitespace-nowrap" dangerouslySetInnerHTML={{ __html: heroData.rating }}></div>
              <span className="sm:w-auto max-w-[98px] sm:max-w-none w-full">
                <img className='w-full' src="/images/trustpilot.svg" alt="" />
              </span>
              <span className="sm:w-auto sm:max-w-none max-w-[98px] w-full">
                <img className='md:w-auto w-[80px] sm:w-[85px]' src="/images/trustpilot-logo.svg" alt="" />
              </span>
            </a>

            {/* feature list mobile */}
            <div className="md:hidden flex justify-center mt-8 md:mt-[30px] mb-7 md:mb-8">
              <div className="max-w-[390px] w-full md:max-w-none flex justify-between gap-1 md:justify-center md:gap-[39px]">
                {heroData.mobileList.map((item, i) => (
                  <div
                    key={i}
                    className="w-fit flex flex-col items-center md:flex-row gap-2 text-[#2B2B2B] text-[14px] md:text-base"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="23.53" height="23.53" rx="11.76" fill="#0BA74A" />
                      <path
                        d="M4.81 12.30L9.91 16.88c.14.12.36.10.48-.04l7.81-9.90"
                        stroke="white"
                        strokeWidth="2.67"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="flex-1">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:hidden mx-auto flex items-center justify-center bg-[#E6F0F5] w-[298px] h-[44px] rounded-[5px] gap-[6px] mb-11">
              <Image
                src="/images/hero-icon.svg"
                alt="Hero icon"
                width={24}
                height={24}
              />
              <div className="text-[#2B2B2B] text-[14px] leading-[14px]">{heroData.riskFree}</div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: desktop-only image */}
        <div className="relative hidden md:block md:w-[270px] md:h-[270px] mt-[14px]">
          <Image
            src={heroData.image}
            alt="Hero"
            fill
            style={{ objectFit: 'cover' }}
            sizes="270px"
            priority
            placeholder="blur"
          />
        </div>
      </div>

      {/* feature list desktop */}
      <div className="hidden md:flex ml-4 justify-center mt-6 md:my-[62px] mb-5 text-[17px]">
        <div className="flex gap-[106px]">
          {heroData.list.map((item, i) => (
            <div key={i} className="flex w-fit text-[#2B2B2B]">
              <div className="flex justify-center items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="23.53" height="23.53" rx="11.76" fill="#0BA74A" />
                  <path
                    d="M4.81 12.30L9.91 16.88c.14.12.36.10.48-.04l7.81-9.90"
                    stroke="white"
                    strokeWidth="2.67"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="leading-[18px] text-nowrap">{item}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}